import { useState, useCallback, useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import * as s from "styles/ModalCommonStyled";
import BigPopupTemplate from "components/templates/modal/big/BigPopupTemplate";
import { ModalInput } from "components/atoms/input/modal/ModalInput";
import Input from "components/atoms/input/Input";
import TextArea from "components/atoms/input/TextArea";
import BigButton from "components/atoms/buttons/BigButton";

import Toggle from "components/atoms/toggle/Toggle";
import PhoneInput from "components/atoms/input/phone/PhoneInput";
import {
  BasePagination,
  SORT_DIRECTION,
  Validate,
} from "api/interfaces/commonInterface.interface";
import {
  UserDto,
  UserGroupInfo,
  UserGroupUserPermission,
} from "api/interfaces/userInterface.interface";
import { getGroupListForEdit } from "api/uGroupAPI";
import type { Option, UserGroupSelectOption } from "utils/options";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";

import { ValidateMsg } from "api/interfaces/commonInterface.interface";
import { Text, ValidateWord } from "components/atoms/text/Text";
import useApiError from "hook/useApiError";
import { useAuth } from "components/Auth";
import Spinner from "components/atoms/loader/Spinner";
import styled from "styled-components";
import { isPhoneNumberValid } from "utils/validHelper";
import * as us from "pages/users/UsersStyled";
import InfoBubble, {
  BubbleContainer,
} from "components/blocks/bubble/InfoBubble";
import { FaInfoCircle } from "react-icons/fa";
import UserGroupSelect from "pages/users/userList/add/UserGroupSelect";
import DeactivateUser from "./DeactivateUser";
import { checkEmpty, toTrim } from "utils/functions";

const makeGroupList = ({
  myGroups = [],
  groupList = [],
}: {
  myGroups: UserGroupInfo[];
  groupList: UserGroupInfo[];
}) => {
  const noResultMyGroup = myGroups.filter(
    (group: UserGroupInfo) =>
      groupList.find((g: UserGroupInfo) => g.groupId === group.groupId) === undefined
  );
  return noResultMyGroup.concat(groupList).map((group: any) => {
    const { groupId, name, enableEdit, userPermission } = group;
    return {
      value: groupId as string,
      label: name,
      disabled:
        !enableEdit ||
        !userPermission.manageUsersAndGroups ||
        noResultMyGroup.some((g) => g.groupId === group.groupId),
      userPermission,
    };
  });
};
interface Props {
  onModal: (type?: string) => void;
  onClickSave: (userDto: UserDto, update?: boolean) => void;
  selectedUser: UserDto;
  isLoading: boolean;
  onSuccessDelete: () => void;
}

export enum UPDATE_TYPE {
  FIRST_NAME,
  LAST_NAME,
  EMAIL,
  GROUP,
  COMPANY_NAME,
  COMPANY_TEL,
  DEPARTMENT,
  USER_GROUP,
  DESC,
  JOB_TITLE,
}

function EditUser(props: Props): JSX.Element {
  const { user } = useAuth();
  const { intlString, lang } = useApiError();

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const [isActivate, setIsActivate] = useState<boolean>(true);
  const [isBubble, setIsBubble] = useState<boolean>(false);

  const [selectGroup, setSelectGroup] = useState<string[]>([]);

  const [validate, setValidate] = useState<Validate>({
    firstName: false,
    lastName: false,
    email: false,
    group: false,
    companyPhone: false,
    description: false,
  });
  const [validateMsg, setValidateMsg] = useState<ValidateMsg>({
    firstName: "",
    lastName: "",
    email: "",
    group: "",
    companyPhone: "",
    description: "",
  });

  const isDisableChange = props.selectedUser?.userId !== user.userId;

  const [userDto, setUserInfo] = useState<UserDto>({ ...props.selectedUser });

  useEffect(() => {
    // console.log(props.selectedUser);
    if (props.selectedUser !== undefined) {
      setUserInfo(props.selectedUser);

      if (props.selectedUser.isActivate !== undefined) {
        setIsActivate(props.selectedUser.isActivate as boolean);
      }
    }
  }, [props.selectedUser]);

  const onChangeUserInfo = useCallback(
    (type: UPDATE_TYPE, value: string | number | string[]) => {
      if (!isDisableChange) {
        if (type === UPDATE_TYPE.FIRST_NAME) {
          setUserInfo((info) => {
            return { ...info, firstName: value as string };
          });
        } else if (type === UPDATE_TYPE.LAST_NAME) {
          setUserInfo((info) => {
            return { ...info, lastName: value as string };
          });
        } else if (type === UPDATE_TYPE.EMAIL) {
          // level 설정 client/server 상위 선택못하게 막아야 함
          setUserInfo((info) => {
            return { ...info, email: value as string };
          });
        } else if (type === UPDATE_TYPE.COMPANY_NAME) {
          setUserInfo((info) => {
            return { ...info, companyName: value as string };
          });
        } else if (type === UPDATE_TYPE.COMPANY_TEL) {
          setUserInfo((info) => {
            return { ...info, companyPhoneNumber: value as string };
          });
        } else if (type === UPDATE_TYPE.DEPARTMENT) {
          setUserInfo((info) => {
            return { ...info, department: value as string };
          });
        } else if (type === UPDATE_TYPE.JOB_TITLE) {
          setUserInfo((info) => {
            return { ...info, jobTitle: value as string };
          });
        }
      }
      if (type === UPDATE_TYPE.GROUP) {
        setUserInfo((info) => {
          // setSelectGroup(value.);
          const userGroups = (value as string[]).map((groupId) => ({
            groupId: String(groupId),
          })) as UserGroupInfo[];
          return { ...info, userGroups };
        });
        setSelectGroup(value as string[]);
      } else if (type === UPDATE_TYPE.DESC) {
        setUserInfo((info) => {
          return { ...info, description: value as string };
        });
      }
    },
    []
  );

  const queryInfo: BasePagination = {
    total: 0,
    pageNum: 0,
    pageLimit: 1000,
    keyword: "",
    sortType: "name",
    sortDirection: SORT_DIRECTION.ASC,
  };

  const getGroupList = useQuery(
    ["group", queryInfo, selectedAccount.accountId],
    () =>
      getGroupListForEdit({
        payload: queryInfo,
        accountId: selectedAccount.accountId,
      }),
    {
      enabled: !!selectedAccount.accountId,
      retry: 0,
      refetchOnWindowFocus: false,
    }
  );

  const onSaveBefore = useCallback(() => {
    setValidate((info) => {
      return {
        ...info,
        firstName: false,
        lastName: false,
        email: false,
        group: false,
        companyPhone: false,
        description: false,
      };
    });
    setValidateMsg((info) => {
      return {
        ...info,
        firstName: "",
        lastName: "",
        email: "",
        group: "",
        companyPhone: "",
        description: "",
      };
    });
    const info: UserDto = {
      ...userDto,
      email: toTrim(userDto.email),
      firstName: toTrim(userDto.firstName),
      lastName: toTrim(userDto.lastName),
      companyName: toTrim(userDto.companyName),
      department: toTrim(userDto.department),
      jobTitle: toTrim(userDto.jobTitle),
      description: toTrim(userDto.description),
    };
    setUserInfo(info);

    let checkedFlag = false;

    if (!isDisableChange) {
      if (checkEmpty(info.firstName)) {
        setValidate((info) => {
          return { ...info, firstName: true };
        });
        setValidateMsg((info) => {
          return {
            ...info,
            firstName: intlString(lang.validateMsg.user.name),
          };
        });
        return;
      }
      if (checkEmpty(info.lastName)) {
        setValidate((info) => {
          return { ...info, lastName: true };
        });
        setValidateMsg((info) => {
          return {
            ...info,
            lastName: intlString(lang.validateMsg.user.name),
          };
        });
        return;
      }

      if (
        !checkEmpty(userDto.companyPhoneNumber) &&
        !isPhoneNumberValid(userDto.companyPhoneNumber)
      ) {
        setValidate((info) => {
          return { ...info, companyPhone: true };
        });
        setValidateMsg((info) => {
          return {
            ...info,
            companyPhone: intlString(lang.validateMsg.profile.phone.validate),
          };
        });
        return;
      }
    }

    if (userDto.userGroups === undefined || userDto.userGroups.length === 0) {
      checkedFlag = true;
      setValidate((info) => {
        return { ...info, group: true };
      });
      setValidateMsg((info) => {
        return {
          ...info,
          group: intlString(lang.validateMsg.user.group.selected),
        };
      });
    }

    if (!checkEmpty(userDto.description) && userDto.description.length > 256) {
      setValidate((info) => {
        return { ...info, description: false };
      });
      setValidateMsg((info) => {
        return {
          ...info,
          description: intlString(lang.validateMsg.profile.description),
        };
      });
      return;
    }

    if (checkedFlag) {
      return;
    }

    props.onClickSave(info, true);
  }, [props, userDto]);

  const groups = useMemo(() => {
    return makeGroupList({
      myGroups: props.selectedUser.userGroups,
      groupList: getGroupList.data?.result || [],
    });
  }, [
    getGroupList.data?.result,
    props.selectedUser.userGroups,
  ]);

  useEffect(() => {
    if (groups && groups.length > 0) {
      setSelectGroup(
        groups.filter((group: UserGroupSelectOption) =>
          (props.selectedUser.userGroups ?? []).some((g) => g.groupId === group.value)
        ).map((group: UserGroupSelectOption) => group.value)
      );
    }
  }, [groups, props.selectedUser.userGroups]);

  return (
    <BigPopupTemplate title={"Edit User"} onModal={() => props.onModal("edit")}>
      <Inner>
        {props.isLoading && (
          <s.LoadingWrapper>
            <Spinner />
          </s.LoadingWrapper>
        )}

        <s.Row>
          <ModalInput label="Email" essential half>
            <Input
              autoComplete="off"
              type="search"
              placeholder="Please enter an email"
              disabled
              pattern="[A-Za-z]+"
              maxLength={100}
              value={userDto.email}
              onChange={(e) => {
                onChangeUserInfo(
                  UPDATE_TYPE.EMAIL,
                  toTrim(e.target.value)
                );
              }}
              isValidated={validate.email}
              data-testid="users-add-input-change-user-info"
            />

            <ValidateWord data-testid="users-add-validate-email">
              {validateMsg.email}
            </ValidateWord>
          </ModalInput>
          <ModalInput label="User Groups" essential half>
            <UserGroupSelect
              options={groups}
              loading={getGroupList.isFetching}
              value={selectGroup}
              disabled={!isActivate as boolean || props.selectedUser.isTemporaryAccess}
              onChange={(value) => onChangeUserInfo(UPDATE_TYPE.GROUP, value)}
            />

            <ValidateWord data-testid="users-add-validate-group">
              {validateMsg.group}
            </ValidateWord>
          </ModalInput>
        </s.Row>
        <s.Row>
          <ModalInput label="First Name" essential>
            <Input
              value={userDto.firstName}
              placeholder="Please enter a first name"
              disabled={isDisableChange}
              maxLength={100}
              onChange={(e) => {
                onChangeUserInfo(UPDATE_TYPE.FIRST_NAME, e.target.value);
              }}
              isValidated={validate.firstName}
              data-testid="users-add-input-change-user-info-2"
            />

            <ValidateWord data-testid="users-add-validate-first-name">
              {validateMsg.firstName}
            </ValidateWord>
          </ModalInput>
          <ModalInput label="Last Name" essential>
            <Input
              value={userDto.lastName}
              placeholder="Please enter a last name"
              disabled={isDisableChange}
              maxLength={100}
              onChange={(e) => {
                onChangeUserInfo(UPDATE_TYPE.LAST_NAME, e.target.value);
              }}
              isValidated={validate.lastName}
              data-testid="users-add-input-change-user-info-3"
            />

            <ValidateWord data-testid="users-add-validate-last-name">
              {validateMsg.lastName}
            </ValidateWord>
          </ModalInput>
        </s.Row>
        <us.EditUserCompany>
          <Text bold>Company</Text>
          <s.Row>
            <ModalInput label="Name" half>
              <Input
                value={userDto.companyName}
                placeholder="Please enter a name"
                disabled={isDisableChange}
                maxLength={100}
                onChange={(e) => {
                  onChangeUserInfo(UPDATE_TYPE.COMPANY_NAME, e.target.value);
                }}
                data-testid="users-add-input-change-user-info-4"
              />
            </ModalInput>

            <ModalInput label="Phone" half>
              <PhoneInput
                selected={userDto.companyPhoneNumber}
                disabled={isDisableChange}
                placeholder="Enter number"
                onChange={(value: string) => {
                  onChangeUserInfo(UPDATE_TYPE.COMPANY_TEL, value);
                }}
                isValidated={validate.companyPhone}
                data-testid="users-add-phone-input-change-user-info"
              />

              <ValidateWord data-testid="users-add-validate-company-phone">
                {validateMsg.companyPhone}
              </ValidateWord>
            </ModalInput>
          </s.Row>
          <s.Row>
            <ModalInput label="Department" half>
              <Input
                value={userDto.department}
                placeholder="Enter department."
                disabled={isDisableChange}
                maxLength={100}
                onChange={(e) => {
                  onChangeUserInfo(UPDATE_TYPE.DEPARTMENT, e.target.value);
                }}
                data-testid="users-add-input-change-user-info-5"
              />
            </ModalInput>

            <ModalInput label="Job Title" half>
              <Input
                value={userDto.jobTitle}
                placeholder="Enter jobTitle."
                disabled={isDisableChange}
                maxLength={100}
                onChange={(e) => {
                  onChangeUserInfo(UPDATE_TYPE.JOB_TITLE, e.target.value);
                }}
                data-testid="users-add-input-change-user-info-6"
              />
            </ModalInput>
          </s.Row>
        </us.EditUserCompany>

        <us.EditUserCompany>
          <us.ReceiveTitle>
            Receive
            <button
              onMouseEnter={() => setIsBubble(true)}
              onMouseLeave={() => setIsBubble(false)}
            >
              <FaInfoCircle />
              {isBubble && (
                <BubbleContainer className="bubble-container">
                  <InfoBubble left>
                    Notification settings can only be configured on the personal
                    profile page.
                  </InfoBubble>
                </BubbleContainer>
              )}
            </button>
          </us.ReceiveTitle>
          <s.ToggleWrapper>
            <s.ToggleItem>
              Receive alert emails
              <Toggle
                isToggled={userDto.notifyAlertMail}
                checked={userDto.notifyAlertMail}
                disabled
                dataTestId="users-add-receive-alert-emails-toggle"
              />
            </s.ToggleItem>
            <s.ToggleItem>
              Receive report emails
              <Toggle
                isToggled={userDto.notifyReportMail}
                checked={userDto.notifyReportMail}
                disabled
                dataTestId="users-add-receive-report-emails-toggle"
              />
            </s.ToggleItem>
            <s.ToggleItem>
              Receive push notifications
              <Toggle
                isToggled={userDto.notifyPush}
                checked={userDto.notifyPush}
                disabled
                dataTestId="users-add-receive-push-notifications-toggle"
              />
            </s.ToggleItem>
          </s.ToggleWrapper>
        </us.EditUserCompany>
        <ModalInput label="Description" isValidated={validate.description}>
          <TextArea
            value={userDto.description}
            placeholder="Please enter a description"
            disabled={!isActivate as boolean}
            maxLength={200}
            onChange={(e) => {
              onChangeUserInfo(UPDATE_TYPE.DESC, e.target.value);
            }}
            data-testid="users-add-textarea-change-user-info"
          />

          <ValidateWord data-testid="users-add-validate-description">
            {validateMsg.description}
          </ValidateWord>
        </ModalInput>
        <s.ButtonWrapper>
          <DeactivateUser
            email={userDto.email}
            onSuccessDelete={props.onSuccessDelete}
            userId={userDto.userId}
          />

          <BigButton
            label="Save"
            onClickBtn={onSaveBefore}
            disabled={!isActivate as boolean}
            data-testid="users-add-button-save-before"
          />
        </s.ButtonWrapper>
      </Inner>
    </BigPopupTemplate>
  );
}

export default EditUser;

const Inner = styled.div`
  position: relative;
  width: 100%;
`;
